import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useSubscription } from '@apollo/client/react';
import { Avatar, Box, Button, Card, Flex, Heading, HStack, Link, Menu, Portal, Text, VStack, Wrap } from '@chakra-ui/react';
import { AiAvatarBadge } from '@src/components/sco/Account/UserAvatar';
import { ASK_PROMPTS } from '@src/components/sco/Advisors/data/askPrompts';
import { advisorsBreadcrumbsWithTrail, useAdvisorsToolbar } from '@src/components/sco/Advisors/hooks/useAdvisorsToolbar';
import { useIsMobile } from '@src/components/sco/Advisors/hooks/useIsMobile';
import { useWilMessageUsage } from '@src/components/sco/Advisors/hooks/useWilMessageUsage';
import { useWilSessions } from '@src/components/sco/Advisors/hooks/useWilSessions';
import { useWilTool } from '@src/components/sco/Advisors/hooks/useWilTool';
import { useSetAdvisorsBelowToolbar, useSetAdvisorsToolbarRightElements } from '@src/components/sco/Advisors/layout/AdvisorsToolbarExtras';
import { WilWhoIsModal } from '@src/components/sco/Advisors/modals/WilWhoIsModal';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { WilMessagePaywall } from '@src/components/sco/Advisors/shared/WilMessagePaywall';
import { clipTitle, isMeaningfulTitle, sessionSubtitle, sessionTitle } from '@src/components/sco/Advisors/utils/wilSessionTitle';
import { ChatMessageComposer } from '@src/components/sco/Chat/ChatMessageComposer';
import { ChatThread } from '@src/components/sco/Chat/ChatThread';
import type { ScoToolSession } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LuCheck, LuChevronDown, LuMessageSquarePlus } from 'react-icons/lu';
const WIL_SESSION_QUERY = gql`
  query AdvisorWilSession($toolSessionId: String!) {
    scoToolSession(toolSessionId: $toolSessionId) {
      id
      toolSessionId
      title
      createdAt
      updatedAt
      generatedSummary
      threadId
      thread {
        id
        threadId
        commentsCount
        lastActiveAt
        comments(pageSize: 5, order: [["createdAt", "asc"]]) {
          id
          items {
            id
            body
            createdByUserId
            createdByUser {
              id
              userId
            }
          }
        }
      }
    }
  }
`;
const WIL_THREAD_COMMENTS_COUNT = gql`
  query WilThreadCommentsCount($threadId: String!) {
    forum {
      id
      thread(threadId: $threadId) {
        id
        commentsCount
      }
    }
  }
`;
const UPSERT_TOOL_SESSION = gql`
  mutation CreateAdvisorWilSession($input: UpsertToolSessionInput!) {
    upsertToolSession(input: $input) {
      toolSession {
        id
        toolSessionId
        title
        threadId
        thread {
          id
          threadId
        }
      }
    }
  }
`;
const WIL_THREAD_COMMENT_SUBSCRIPTION = gql`
  subscription AdvisorWilThreadComment($threadId: String!) {
    scoThreadComment(threadId: $threadId) {
      id
      commentId
    }
  }
`;

/**
 * A plain module function, not part of the component: the React Compiler (1.0.0) skips a
 * component whose try/catch contains optional chaining.
 */
async function createWilSession(createSession: (options: {
  variables: {
    input: {
      toolId: string;
    };
  };
}) => Promise<any>, toolId: string): Promise<string | null> {
  try {
    const res = await createSession({
      variables: {
        input: {
          toolId
        }
      }
    });
    const newId = res.data?.upsertToolSession?.toolSession?.toolSessionId;
    return newId ? String(newId) : null;
  } catch (err) {
    console.error('Failed to create Wil session', err);
    return null;
  }
}
export const AskSection = () => {
  const $ = _c(194);
  const paths = useAdvisorsPaths();
  const router = useRouter();
  const {
    currentUser
  } = useCurrentUser();
  const isMobile = useIsMobile();
  const wilUsage = useWilMessageUsage();
  const [hasMessagesInThread, setHasMessagesInThread] = useState(false);
  const [threadRefetchTrigger, setThreadRefetchTrigger] = useState(0);
  const [wilWhoIsOpen, setWilWhoIsOpen] = useState(false);
  const autoCreateStarted = useRef(false);
  const forceNewStarted = useRef(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ChatMessageComposer.mutations.upsertChatComment();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [upsertChatComment, upsertChatCommentMutation] = useMutation(t0);
  const toolSessionIdFromQuery = typeof router.query.toolSessionId === "string" ? router.query.toolSessionId : undefined;
  const forceNew = router.query.new === "1" || router.query.new === "true";
  const forceList = router.query.list === "1" || router.query.list === "true";
  const {
    toolQuery,
    toolId,
    toolIconUrl,
    toolUserId,
    wilDisplayName
  } = useWilTool();
  const t1 = currentUser?.userId;
  let t2;
  if ($[1] !== t1 || $[2] !== toolId || $[3] !== toolSessionIdFromQuery) {
    t2 = {
      toolId,
      userId: t1,
      activeToolSessionId: toolSessionIdFromQuery
    };
    $[1] = t1;
    $[2] = toolId;
    $[3] = toolSessionIdFromQuery;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    sessionsQuery,
    sessions,
    visibleSessions,
    hasMoreSessions,
    loadMoreSessions
  } = useWilSessions(t2);
  let t3;
  if ($[5] !== toolSessionIdFromQuery) {
    t3 = {
      toolSessionId: toolSessionIdFromQuery
    };
    $[5] = toolSessionIdFromQuery;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = !toolSessionIdFromQuery;
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = {
      variables: t3,
      skip: t4,
      fetchPolicy: "cache-and-network"
    };
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const activeSessionQuery = useQuery(WIL_SESSION_QUERY, t5);
  const [createSession, t6] = useMutation(UPSERT_TOOL_SESSION);
  const {
    loading: creatingSession
  } = t6;
  activeSessionQuery.data?.scoToolSession?.threadId;
  activeSessionQuery.data?.scoToolSession?.thread?.threadId;
  let t7;
  bb0: {
    const fromActive = activeSessionQuery.data?.scoToolSession?.threadId || activeSessionQuery.data?.scoToolSession?.thread?.threadId;
    if (fromActive) {
      t7 = String(fromActive);
      break bb0;
    }
    let t8;
    if ($[10] !== sessions || $[11] !== toolSessionIdFromQuery) {
      let t9;
      if ($[13] !== toolSessionIdFromQuery) {
        t9 = s => s.toolSessionId === toolSessionIdFromQuery;
        $[13] = toolSessionIdFromQuery;
        $[14] = t9;
      } else {
        t9 = $[14];
      }
      t8 = sessions.find(t9);
      $[10] = sessions;
      $[11] = toolSessionIdFromQuery;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    const fromList = t8;
    const listThreadId = fromList?.threadId || fromList?.thread?.threadId;
    t7 = listThreadId ? String(listThreadId) : undefined;
  }
  const activeThreadId = t7;
  const routerRef = useRef(router);
  let t8;
  if ($[15] !== router) {
    t8 = () => {
      routerRef.current = router;
    };
    $[15] = router;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  useEffect(t8);
  let t9;
  if ($[17] !== paths) {
    t9 = toolSessionId => {
      routerRef.current.push(paths.withQuery(paths.ask, {
        toolSessionId
      }), undefined, {
        shallow: true
      });
    };
    $[17] = paths;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const openSession = t9;
  let t10;
  if ($[19] !== paths) {
    t10 = () => {
      routerRef.current.push(paths.withQuery(paths.ask, {
        list: "1"
      }), undefined, {
        shallow: true
      });
    };
    $[19] = paths;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const goToSessionList = t10;
  let t11;
  if ($[21] !== createSession || $[22] !== creatingSession || $[23] !== openSession || $[24] !== sessionsQuery || $[25] !== toolId || $[26] !== wilUsage.isLimitReached) {
    t11 = async () => {
      if (!toolId || creatingSession) {
        return;
      }
      if (wilUsage.isLimitReached) {
        return;
      }
      const newId = await createWilSession(createSession, toolId);
      if (newId) {
        ;
        try {
          await sessionsQuery.refetch();
        } catch (t12) {
          const err = t12;
          console.error("Failed to create Wil session", err);
        }
        openSession(newId);
      }
    };
    $[21] = createSession;
    $[22] = creatingSession;
    $[23] = openSession;
    $[24] = sessionsQuery;
    $[25] = toolId;
    $[26] = wilUsage.isLimitReached;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const startNewSession = t11;
  let t12;
  let t13;
  if ($[28] !== creatingSession || $[29] !== forceList || $[30] !== forceNew || $[31] !== router.isReady || $[32] !== sessions.length || $[33] !== sessionsQuery.loading || $[34] !== startNewSession || $[35] !== toolId || $[36] !== toolSessionIdFromQuery || $[37] !== wilUsage.isLimitReached || $[38] !== wilUsage.loading) {
    t12 = () => {
      if (!router.isReady || !toolId) {
        return;
      }
      if (toolSessionIdFromQuery || forceNew || forceList) {
        return;
      }
      if (sessionsQuery.loading || wilUsage.loading) {
        return;
      }
      if (wilUsage.isLimitReached) {
        return;
      }
      if (sessions.length > 0) {
        return;
      }
      if (autoCreateStarted.current || creatingSession) {
        return;
      }
      autoCreateStarted.current = true;
      startNewSession();
    };
    t13 = [router.isReady, toolId, toolSessionIdFromQuery, forceNew, forceList, sessionsQuery.loading, wilUsage.loading, wilUsage.isLimitReached, sessions.length, creatingSession, startNewSession];
    $[28] = creatingSession;
    $[29] = forceList;
    $[30] = forceNew;
    $[31] = router.isReady;
    $[32] = sessions.length;
    $[33] = sessionsQuery.loading;
    $[34] = startNewSession;
    $[35] = toolId;
    $[36] = toolSessionIdFromQuery;
    $[37] = wilUsage.isLimitReached;
    $[38] = wilUsage.loading;
    $[39] = t12;
    $[40] = t13;
  } else {
    t12 = $[39];
    t13 = $[40];
  }
  useEffect(t12, t13);
  const onlySessionId = sessions.length === 1 ? sessions[0]?.toolSessionId : undefined;
  let t14;
  let t15;
  if ($[41] !== forceList || $[42] !== forceNew || $[43] !== onlySessionId || $[44] !== openSession || $[45] !== router.isReady || $[46] !== sessionsQuery.loading || $[47] !== toolId || $[48] !== toolSessionIdFromQuery) {
    t14 = () => {
      if (!router.isReady || !toolId) {
        return;
      }
      if (toolSessionIdFromQuery || forceNew || forceList) {
        return;
      }
      if (sessionsQuery.loading) {
        return;
      }
      if (!onlySessionId) {
        return;
      }
      openSession(String(onlySessionId));
    };
    t15 = [router.isReady, toolId, toolSessionIdFromQuery, forceNew, forceList, sessionsQuery.loading, onlySessionId, openSession];
    $[41] = forceList;
    $[42] = forceNew;
    $[43] = onlySessionId;
    $[44] = openSession;
    $[45] = router.isReady;
    $[46] = sessionsQuery.loading;
    $[47] = toolId;
    $[48] = toolSessionIdFromQuery;
    $[49] = t14;
    $[50] = t15;
  } else {
    t14 = $[49];
    t15 = $[50];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[51] !== creatingSession || $[52] !== forceNew || $[53] !== router.isReady || $[54] !== startNewSession || $[55] !== toolId) {
    t16 = () => {
      if (!router.isReady || !toolId || !forceNew) {
        return;
      }
      if (forceNewStarted.current || creatingSession) {
        return;
      }
      forceNewStarted.current = true;
      startNewSession();
    };
    t17 = [router.isReady, toolId, forceNew, creatingSession, startNewSession];
    $[51] = creatingSession;
    $[52] = forceNew;
    $[53] = router.isReady;
    $[54] = startNewSession;
    $[55] = toolId;
    $[56] = t16;
    $[57] = t17;
  } else {
    t16 = $[56];
    t17 = $[57];
  }
  useEffect(t16, t17);
  let t18;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => {
      setHasMessagesInThread(false);
    };
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  let t19;
  if ($[59] !== activeThreadId) {
    t19 = [activeThreadId];
    $[59] = activeThreadId;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  useEffect(t18, t19);
  let t20;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = hasMessages => {
      setHasMessagesInThread(prev => prev === hasMessages ? prev : hasMessages);
    };
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  const handleHasMessages = t20;
  let t21;
  if ($[62] !== activeThreadId) {
    t21 = {
      threadId: activeThreadId
    };
    $[62] = activeThreadId;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  const t22 = !activeThreadId;
  let t23;
  if ($[64] !== t21 || $[65] !== t22) {
    t23 = {
      variables: t21,
      skip: t22
    };
    $[64] = t21;
    $[65] = t22;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  const commentsQuery = useQuery(WIL_THREAD_COMMENTS_COUNT, t23);
  const refetchComments = commentsQuery.refetch;
  let t24;
  if ($[67] !== activeThreadId || $[68] !== refetchComments || $[69] !== upsertChatComment || $[70] !== upsertChatCommentMutation.loading || $[71] !== wilUsage) {
    t24 = body => {
      if (!activeThreadId || wilUsage.isLimitReached || upsertChatCommentMutation.loading) {
        return;
      }
      upsertChatComment({
        variables: {
          input: {
            threadId: activeThreadId,
            body
          }
        }
      }).then(() => {
        setHasMessagesInThread(true);
        setThreadRefetchTrigger(_temp);
        refetchComments();
        if (wilUsage.isWilMessageLimited) {
          wilUsage.refetch?.();
        }
      });
    };
    $[67] = activeThreadId;
    $[68] = refetchComments;
    $[69] = upsertChatComment;
    $[70] = upsertChatCommentMutation.loading;
    $[71] = wilUsage;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  const onStartPromptClick = t24;
  const sessionsRefreshTimer = useRef(null);
  let t25;
  let t26;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => () => {
      if (sessionsRefreshTimer.current) {
        clearTimeout(sessionsRefreshTimer.current);
      }
    };
    t26 = [];
    $[73] = t25;
    $[74] = t26;
  } else {
    t25 = $[73];
    t26 = $[74];
  }
  useEffect(t25, t26);
  const lastCommentId = useRef(null);
  let t27;
  if ($[75] !== activeThreadId) {
    t27 = {
      threadId: activeThreadId
    };
    $[75] = activeThreadId;
    $[76] = t27;
  } else {
    t27 = $[76];
  }
  const t28 = !activeThreadId;
  let t29;
  if ($[77] !== refetchComments || $[78] !== sessionsQuery || $[79] !== wilUsage) {
    t29 = t30 => {
      const {
        data
      } = t30;
      const commentId = (data.data as any)?.scoThreadComment?.commentId || null;
      if (commentId && commentId === lastCommentId.current) {
        return;
      }
      lastCommentId.current = commentId;
      refetchComments();
      if (sessionsRefreshTimer.current) {
        clearTimeout(sessionsRefreshTimer.current);
      }
      sessionsRefreshTimer.current = setTimeout(() => {
        sessionsQuery.refetch();
        if (wilUsage.isWilMessageLimited) {
          wilUsage.refetch?.();
        }
      }, 2000);
    };
    $[77] = refetchComments;
    $[78] = sessionsQuery;
    $[79] = wilUsage;
    $[80] = t29;
  } else {
    t29 = $[80];
  }
  let t30;
  if ($[81] !== t27 || $[82] !== t28 || $[83] !== t29) {
    t30 = {
      variables: t27,
      skip: t28,
      ignoreResults: true,
      onData: t29
    };
    $[81] = t27;
    $[82] = t28;
    $[83] = t29;
    $[84] = t30;
  } else {
    t30 = $[84];
  }
  useSubscription(WIL_THREAD_COMMENT_SUBSCRIPTION, t30);
  const messageCount = commentsQuery.data?.forum?.thread?.commentsCount ?? 0;
  const threadHasMessages = hasMessagesInThread || messageCount > 0;
  const isCheckingMessages = !!activeThreadId && commentsQuery.loading && !hasMessagesInThread;
  const showIntroHeader = !!activeThreadId && !isCheckingMessages && !threadHasMessages;
  const isChatView = !!toolSessionIdFromQuery;
  const isListView = !isChatView && sessions.length > 0 && !creatingSession && !forceNew && (sessions.length > 1 || forceList);
  activeSessionQuery.data?.scoToolSession;
  let t31;
  if ($[85] !== activeSessionQuery.data?.scoToolSession || $[86] !== sessions || $[87] !== toolSessionIdFromQuery || $[88] !== toolUserId || $[89] !== wilDisplayName) {
    bb1: {
      if (!toolSessionIdFromQuery) {
        t31 = null;
        break bb1;
      }
      let t32;
      if ($[91] !== toolSessionIdFromQuery) {
        t32 = s_0 => s_0.toolSessionId === toolSessionIdFromQuery;
        $[91] = toolSessionIdFromQuery;
        $[92] = t32;
      } else {
        t32 = $[92];
      }
      const fromList_0 = sessions.find(t32);
      if (fromList_0) {
        t31 = sessionTitle(fromList_0, toolUserId);
        break bb1;
      }
      const loaded = activeSessionQuery.data?.scoToolSession;
      if (loaded) {
        const t33 = loaded as ScoToolSession;
        let t34;
        if ($[93] !== t33 || $[94] !== toolUserId) {
          t34 = sessionTitle(t33, toolUserId);
          $[93] = t33;
          $[94] = toolUserId;
          $[95] = t34;
        } else {
          t34 = $[95];
        }
        t31 = t34;
        break bb1;
      }
      t31 = wilDisplayName;
    }
    $[85] = activeSessionQuery.data?.scoToolSession;
    $[86] = sessions;
    $[87] = toolSessionIdFromQuery;
    $[88] = toolUserId;
    $[89] = wilDisplayName;
    $[90] = t31;
  } else {
    t31 = $[90];
  }
  const activeSessionTitle = t31;
  const t32 = `Ask ${wilDisplayName}`;
  let t33;
  if ($[96] !== activeSessionTitle || $[97] !== isChatView || $[98] !== paths.ask || $[99] !== paths.base || $[100] !== t32 || $[101] !== toolSessionIdFromQuery) {
    const trail = [{
      title: t32,
      href: paths.ask
    }];
    if (isChatView && activeSessionTitle) {
      trail.push({
        title: activeSessionTitle,
        href: `${paths.ask}?toolSessionId=${toolSessionIdFromQuery}`
      });
    }
    t33 = advisorsBreadcrumbsWithTrail(paths.base, ...trail);
    $[96] = activeSessionTitle;
    $[97] = isChatView;
    $[98] = paths.ask;
    $[99] = paths.base;
    $[100] = t32;
    $[101] = toolSessionIdFromQuery;
    $[102] = t33;
  } else {
    t33 = $[102];
  }
  const toolbarBreadcrumbs = t33;
  let t34;
  if ($[103] !== toolbarBreadcrumbs) {
    t34 = {
      breadcrumbs: toolbarBreadcrumbs
    };
    $[103] = toolbarBreadcrumbs;
    $[104] = t34;
  } else {
    t34 = $[104];
  }
  useAdvisorsToolbar(t34);
  let t35;
  bb2: {
    if (!isChatView) {
      t35 = null;
      break bb2;
    }
    let t36;
    if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
      t36 = {
        placement: "bottom-end"
      };
      $[105] = t36;
    } else {
      t36 = $[105];
    }
    let t37;
    if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
      t37 = <Menu.Trigger asChild={true}><Button size="sm" variant="outline">Chats<LuChevronDown /></Button></Menu.Trigger>;
      $[106] = t37;
    } else {
      t37 = $[106];
    }
    let t38;
    if ($[107] !== openSession || $[108] !== sessions || $[109] !== toolSessionIdFromQuery || $[110] !== toolUserId) {
      let t39;
      if ($[112] !== openSession || $[113] !== toolSessionIdFromQuery || $[114] !== toolUserId) {
        t39 = session => {
          const id = session.toolSessionId;
          if (!id) {
            return null;
          }
          const isActive = id === toolSessionIdFromQuery;
          return <Menu.Item key={id} closeOnSelect={true} value={String(id)} onClick={() => {
            if (!isActive) {
              openSession(String(id));
            }
          }}><Text flex={1} fontWeight={isActive ? "semibold" : "normal"} lineClamp={1}>{sessionTitle(session, toolUserId)}</Text>{isActive ? <LuCheck /> : null}</Menu.Item>;
        };
        $[112] = openSession;
        $[113] = toolSessionIdFromQuery;
        $[114] = toolUserId;
        $[115] = t39;
      } else {
        t39 = $[115];
      }
      t38 = sessions.map(t39);
      $[107] = openSession;
      $[108] = sessions;
      $[109] = toolSessionIdFromQuery;
      $[110] = toolUserId;
      $[111] = t38;
    } else {
      t38 = $[111];
    }
    let t39;
    if ($[116] !== sessions.length) {
      t39 = sessions.length > 0 ? <Menu.Separator /> : null;
      $[116] = sessions.length;
      $[117] = t39;
    } else {
      t39 = $[117];
    }
    let t40;
    if ($[118] !== startNewSession) {
      t40 = () => {
        startNewSession();
      };
      $[118] = startNewSession;
      $[119] = t40;
    } else {
      t40 = $[119];
    }
    let t41;
    if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
      t41 = <LuMessageSquarePlus />;
      $[120] = t41;
    } else {
      t41 = $[120];
    }
    let t42;
    if ($[121] !== creatingSession || $[122] !== t40) {
      t42 = <Menu.Item disabled={creatingSession} value="new-session" onClick={t40}>{t41}New Chat</Menu.Item>;
      $[121] = creatingSession;
      $[122] = t40;
      $[123] = t42;
    } else {
      t42 = $[123];
    }
    let t43;
    if ($[124] !== t38 || $[125] !== t39 || $[126] !== t42) {
      t43 = <Menu.Root positioning={t36}>{t37}<Portal><Menu.Positioner><Menu.Content maxH="min(24rem, 70vh)" maxW="20rem" minW="16rem" overflowY="auto">{t38}{t39}{t42}</Menu.Content></Menu.Positioner></Portal></Menu.Root>;
      $[124] = t38;
      $[125] = t39;
      $[126] = t42;
      $[127] = t43;
    } else {
      t43 = $[127];
    }
    t35 = t43;
  }
  const toolbarMenu = t35;
  useSetAdvisorsToolbarRightElements(toolbarMenu);
  const showUsageBanner = isChatView && !!activeThreadId && wilUsage.isWilMessageLimited && wilUsage.remainingMessages <= 10;
  let t36;
  bb3: {
    if (!showUsageBanner) {
      t36 = null;
      break bb3;
    }
    const t37 = isMobile ? 3 : 4;
    let t38;
    if ($[128] !== wilUsage.limit || $[129] !== wilUsage.remainingMessages || $[130] !== wilUsage.usedMessages) {
      t38 = <WilMessagePaywall limit={wilUsage.limit} remainingMessages={wilUsage.remainingMessages} usedMessages={wilUsage.usedMessages} variant="banner" />;
      $[128] = wilUsage.limit;
      $[129] = wilUsage.remainingMessages;
      $[130] = wilUsage.usedMessages;
      $[131] = t38;
    } else {
      t38 = $[131];
    }
    let t39;
    if ($[132] !== t37 || $[133] !== t38) {
      t39 = <Box bg="colorPalette.container" borderBottomWidth="1px" borderColor="border" flexShrink={0} paddingX={t37} paddingY={2}>{t38}</Box>;
      $[132] = t37;
      $[133] = t38;
      $[134] = t39;
    } else {
      t39 = $[134];
    }
    t36 = t39;
  }
  const belowToolbarBanner = t36;
  useSetAdvisorsBelowToolbar(belowToolbarBanner);
  const t37 = isMobile ? 4 : 6;
  const t38 = isMobile ? 6 : 10;
  const t39 = isMobile ? 4 : 6;
  const t40 = isMobile ? "xl" : "3xl";
  let t41;
  if ($[135] !== t40) {
    t41 = <VStack gap={1} textAlign="center"><Heading size={t40}>What can we get done today?</Heading></VStack>;
    $[135] = t40;
    $[136] = t41;
  } else {
    t41 = $[136];
  }
  let t42;
  if ($[137] !== t37 || $[138] !== t38 || $[139] !== t39 || $[140] !== t41) {
    t42 = <Box flexShrink={0} overflowY="auto"><VStack align="center" gap={t37} paddingBottom={4} paddingTop={t38} paddingX={t39}>{t41}</VStack></Box>;
    $[137] = t37;
    $[138] = t38;
    $[139] = t39;
    $[140] = t41;
    $[141] = t42;
  } else {
    t42 = $[141];
  }
  const t43 = isMobile ? 2 : 6;
  let t44;
  if ($[142] !== activeThreadId || $[143] !== onStartPromptClick || $[144] !== upsertChatCommentMutation.loading || $[145] !== wilUsage.isLimitReached) {
    t44 = ASK_PROMPTS.map(prompt => <Button key={prompt} borderRadius="full" disabled={!activeThreadId || wilUsage.isLimitReached} height="auto" loading={upsertChatCommentMutation.loading} paddingX={4} paddingY={3} size="xs" variant="outlineGhost" onClick={() => onStartPromptClick(prompt)}>{prompt}</Button>);
    $[142] = activeThreadId;
    $[143] = onStartPromptClick;
    $[144] = upsertChatCommentMutation.loading;
    $[145] = wilUsage.isLimitReached;
    $[146] = t44;
  } else {
    t44 = $[146];
  }
  let t45;
  if ($[147] !== t44) {
    t45 = <Wrap gap={2} justify="center">{t44}</Wrap>;
    $[147] = t44;
    $[148] = t45;
  } else {
    t45 = $[148];
  }
  let t46;
  if ($[149] !== paths.inbox) {
    t46 = <Text color="colorPalette.fg/60" fontSize="sm" textAlign="center"><Link color="orange.fg" fontWeight="semibold" href={paths.inbox}>Talk to a human advisor</Link></Text>;
    $[149] = paths.inbox;
    $[150] = t46;
  } else {
    t46 = $[150];
  }
  let t47;
  if ($[151] !== t43 || $[152] !== t45 || $[153] !== t46) {
    t47 = <VStack gap={3} maxWidth="2xl" paddingX={t43} width="full">{t45}{t46}</VStack>;
    $[151] = t43;
    $[152] = t45;
    $[153] = t46;
    $[154] = t47;
  } else {
    t47 = $[154];
  }
  let t48;
  if ($[155] !== t42 || $[156] !== t47) {
    t48 = <VStack align="center" flex={1} flexGrow={1} justify="center" paddingY={6} width="full">{t42}{t47}</VStack>;
    $[155] = t42;
    $[156] = t47;
    $[157] = t48;
  } else {
    t48 = $[157];
  }
  const blankState = t48;
  const sessionNotFound = isChatView && !activeSessionQuery.loading && !activeThreadId && !activeSessionQuery.error && !creatingSession;
  const waitingForSingleOpen = !isChatView && !forceList && !forceNew && sessions.length === 1 && !toolSessionIdFromQuery;
  const waitingForAutoCreate = !isChatView && !isListView && sessions.length === 0 && !wilUsage.isLimitReached && !forceList;
  const loading = !router.isReady || toolQuery.loading || !!toolId && sessionsQuery.loading && sessions.length === 0 && !toolSessionIdFromQuery || isChatView && activeSessionQuery.loading && !activeThreadId || creatingSession || waitingForSingleOpen || waitingForAutoCreate || wilUsage.isWilMessageLimited && wilUsage.loading && !isChatView && !activeThreadId;
  const t49 = showIntroHeader ? blankState : undefined;
  let t50;
  if ($[158] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = {
      minHeight: 0 as const
    };
    $[158] = t50;
  } else {
    t50 = $[158];
  }
  let t51;
  if ($[159] !== t49 || $[160] !== threadRefetchTrigger) {
    t51 = {
      blankState: t49,
      containerStackProps: t50,
      onHasMessages: handleHasMessages,
      refetchTrigger: threadRefetchTrigger
    };
    $[159] = t49;
    $[160] = threadRefetchTrigger;
    $[161] = t51;
  } else {
    t51 = $[161];
  }
  const chatMessageListProps = t51;
  const error = toolQuery.error || sessionsQuery.error || activeSessionQuery.error;
  const t52 = loading && !sessionNotFound;
  let t53;
  if ($[162] !== activeThreadId || $[163] !== chatMessageListProps || $[164] !== creatingSession || $[165] !== goToSessionList || $[166] !== hasMoreSessions || $[167] !== isChatView || $[168] !== isListView || $[169] !== isMobile || $[170] !== loadMoreSessions || $[171] !== openSession || $[172] !== sessionNotFound || $[173] !== sessions.length || $[174] !== startNewSession || $[175] !== toolIconUrl || $[176] !== toolUserId || $[177] !== visibleSessions || $[178] !== wilDisplayName || $[179] !== wilUsage.isLimitReached || $[180] !== wilUsage.isWilMessageLimited || $[181] !== wilUsage.limit || $[182] !== wilUsage.remainingMessages || $[183] !== wilUsage.usedMessages) {
    t53 = sessionNotFound ? <VStack align="center" flex={1} gap={4} justify="center" padding={6}><Text color="colorPalette.fg/70" textAlign="center">That chat could not be found.</Text><Button variant="outline" onClick={goToSessionList}>Back to all chats</Button></VStack> : isChatView && activeThreadId ? <Box display="flex" flex={1} flexDirection="column" minHeight={0}><ChatThread bypassContentPermissions={true} hideHeader={true} chatMessageListProps={chatMessageListProps} hideComposer={wilUsage.isLimitReached} threadId={activeThreadId} /></Box> : isListView || wilUsage.isLimitReached ? <Flex direction="column" height="full" minHeight={0} width="full"><Box flex={1} minHeight={0} overflowY="auto" paddingTop={8} paddingX={isMobile ? 4 : 6}><VStack align="center" gap={6} marginX="auto" maxWidth="lg" width="full"><VStack gap={2} textAlign="center"><Avatar.Root borderRadius="full" size="xl"><Avatar.Fallback name={wilDisplayName} /><Avatar.Image src={toolIconUrl || "/assets/people/wil.jpg"} /><AiAvatarBadge /></Avatar.Root><Heading size="lg">Chats with {wilDisplayName}</Heading><Text color="colorPalette.fg/60" fontSize="sm">{sessions.length > 0 ? "Pick up a previous conversation or start a new one." : `Your free ${wilDisplayName} messages are used up.`}</Text>{wilUsage.isWilMessageLimited ? <Text color="orange.fg" fontSize="xs">{wilUsage.isLimitReached ? "Free message limit reached" : `${wilUsage.remainingMessages} free messages left`}</Text> : null}</VStack>{wilUsage.isLimitReached ? <WilMessagePaywall limit={wilUsage.limit} remainingMessages={wilUsage.remainingMessages} usedMessages={wilUsage.usedMessages} /> : null}<VStack align="stretch" gap={2} paddingBottom={8} width="full">{visibleSessions.map(session_0 => {
              const id_0 = session_0.toolSessionId;
              if (!id_0) {
                return null;
              }
              const title = sessionTitle(session_0, toolUserId);
              const showSummaryPreview = !!session_0.generatedSummary?.trim() && isMeaningfulTitle(session_0.title) && clipTitle(session_0.generatedSummary.trim().split(/\n/)[0]) !== title;
              return <Card.Root key={id_0} _hover={{
                bg: "colorPalette.subtle"
              }} cursor="pointer" transition="background 120ms ease" variant="outline" onClick={() => openSession(String(id_0))}><Card.Body paddingX={4} paddingY={3}><HStack align="start" gap={3}><Avatar.Root flexShrink={0} size="sm"><Avatar.Fallback name={wilDisplayName} /><Avatar.Image src={toolIconUrl || "/assets/people/wil.jpg"} /><AiAvatarBadge /></Avatar.Root><VStack align="stretch" flex={1} gap={0.5} minW={0}><Text fontWeight="semibold" lineClamp={1}>{title}</Text><Text color="colorPalette.fg/55" fontSize="xs">{sessionSubtitle(session_0, wilDisplayName)}{session_0.thread?.commentsCount ? ` · ${session_0.thread.commentsCount} messages` : ""}</Text>{showSummaryPreview && <Text color="colorPalette.fg/70" fontSize="sm" lineClamp={2}>{session_0.generatedSummary}</Text>}</VStack></HStack></Card.Body></Card.Root>;
            })}{hasMoreSessions ? <Button variant="outline" width="full" onClick={loadMoreSessions}>Load more</Button> : null}{!wilUsage.isLimitReached ? <Button loading={creatingSession} variant="cta" width="full" onClick={() => {
              startNewSession();
            }}><LuMessageSquarePlus />New Chat</Button> : null}<Text color="colorPalette.fg/60" fontSize="sm" paddingTop={1} textAlign="center"><Link color="orange.fg" cursor="pointer" fontWeight="semibold" onClick={() => setWilWhoIsOpen(true)}>Who is Wil.ai?</Link></Text></VStack></VStack></Box></Flex> : null;
    $[162] = activeThreadId;
    $[163] = chatMessageListProps;
    $[164] = creatingSession;
    $[165] = goToSessionList;
    $[166] = hasMoreSessions;
    $[167] = isChatView;
    $[168] = isListView;
    $[169] = isMobile;
    $[170] = loadMoreSessions;
    $[171] = openSession;
    $[172] = sessionNotFound;
    $[173] = sessions.length;
    $[174] = startNewSession;
    $[175] = toolIconUrl;
    $[176] = toolUserId;
    $[177] = visibleSessions;
    $[178] = wilDisplayName;
    $[179] = wilUsage.isLimitReached;
    $[180] = wilUsage.isWilMessageLimited;
    $[181] = wilUsage.limit;
    $[182] = wilUsage.remainingMessages;
    $[183] = wilUsage.usedMessages;
    $[184] = t53;
  } else {
    t53 = $[184];
  }
  let t54;
  if ($[185] !== error || $[186] !== t52 || $[187] !== t53) {
    t54 = <AdvisorsQueryState error={error} loading={t52}>{t53}</AdvisorsQueryState>;
    $[185] = error;
    $[186] = t52;
    $[187] = t53;
    $[188] = t54;
  } else {
    t54 = $[188];
  }
  let t55;
  if ($[189] !== wilWhoIsOpen) {
    t55 = <WilWhoIsModal open={wilWhoIsOpen} onOpenChange={setWilWhoIsOpen} />;
    $[189] = wilWhoIsOpen;
    $[190] = t55;
  } else {
    t55 = $[190];
  }
  let t56;
  if ($[191] !== t54 || $[192] !== t55) {
    t56 = <Flex direction="column" height="full" minHeight={0} position="relative" width="full">{t54}{t55}</Flex>;
    $[191] = t54;
    $[192] = t55;
    $[193] = t56;
  } else {
    t56 = $[193];
  }
  return t56;
};
function _temp(x) {
  return x + 1;
}